import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCompare } from '../context/CompareContext';
import CategoryPerformers from '../components/CategoryPerformers'; // <-- The new, interactive component
import { FaSearch, FaSpinner, FaChevronLeft } from 'react-icons/fa'; // <-- Added FaChevronLeft

// B. The main Dashboard component
function MutualFundDashboard() {
  // --- STATE ---
  const [allFunds, setAllFunds] = useState([]); // Master list (50,000+)
  const [filteredFunds, setFilteredFunds] = useState([]); // Search results
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true); // For initial page load
  const [error, setError] = useState(null);
  const { addFundToCompare } = useCompare();

  // --- EFFECTS ---

  // 1. Fetch the master list of all funds ONCE on page load
  useEffect(() => {
    const fetchAllFunds = async () => {
      try {
        const response = await fetch('https://api.mfapi.in/mf');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setAllFunds(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllFunds();
  }, []); // Empty array = runs once

  // 2. Filter the funds whenever the search term changes
  useEffect(() => {
    // CRITICAL: Only filter if search is 3+ chars.
    if (searchTerm.length < 3) {
      setFilteredFunds([]); // Clear results
      return;
    }

    const lowerCaseSearch = searchTerm.toLowerCase();
    const results = allFunds.filter(fund =>
      fund.schemeName.toLowerCase().includes(lowerCaseSearch)
    );
    
    // Limit results to 100 to keep the UI fast
    setFilteredFunds(results.slice(0, 100));

  }, [searchTerm, allFunds]); // Re-runs when user types or funds load

  // --- RENDER LOGIC ---

  // 3. Show a full-page loader while the master list is fetched
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <FaSpinner className="animate-spin text-blue-600 h-12 w-12" />
        <span className="ml-4 text-xl text-gray-700">Loading Fund Data...</span>
      </div>
    );
  }

  // 4. Show a full-page error if the API fails
  if (error) {
    return (
      <div className="text-center p-12 bg-gray-100 min-h-screen">
        <h2 className="text-2xl font-bold text-red-600">Error</h2>
        <p className="text-gray-700 mt-2">Could not load mutual fund data. Please try again later.</p>
        <p className="text-sm text-gray-500 mt-1">Details: {error}</p>
      </div>
    );
  }

  // 5. This is the main "Dashboard" content (shown by default)
  // --- THIS IS THE UPDATED PART ---
  const DashboardContent = () => (
    <div className="my-12">
      <CategoryPerformers />
    </div>
  );

  // 6. This is the "Search Results" content (shown when user is typing)
  // --- THIS IS YOUR CODE WITH THE FIXES ---
  const SearchResultsContent = () => (
    <div className="my-12">
      {/* --- "Back" Button --- */}
      <button
        onClick={() => setSearchTerm('')} // This clears the search
        className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center mb-4"
      >
        &larr; Back to Dashboard
      </button>
      
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Search Results {filteredFunds.length > 0 && `(Showing ${filteredFunds.length} of ${allFunds.filter(f => f.schemeName.toLowerCase().includes(searchTerm.toLowerCase())).length})`}
      </h2>
      
      {filteredFunds.length === 0 && (
        <p className="text-gray-500 text-center">No funds found matching your search.</p>
      )}
      
      <ul className="space-y-3">
        {filteredFunds.map(fund => (
          <li 
            key={fund.schemeCode} 
            className="flex justify-between items-center p-4 bg-white rounded-lg shadow-md border border-gray-200 hover:bg-gray-50"
          >
            {/* Fund Name & Code */}
            <div>
              <Link 
                to={`/mutual-funds/${fund.schemeCode}`}
                className="font-medium text-blue-600 hover:text-blue-800"
              >
                {fund.schemeName}
              </Link>
              <p className="text-sm text-gray-500">Code: {fund.schemeCode}</p>
            </div>
            
            {/* Compare Button */}
            <button
              onClick={() => addFundToCompare({ schemeCode: fund.schemeCode, schemeName: fund.schemeName })}
              className="ml-4 py-1 px-3 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 flex-shrink-0"
            >
              + Compare
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  // --- FINAL RENDER ---
  return (
    // --- UPDATED LAYOUT ---
    <div className="min-h-screen bg-[#F5F7FB]">
      
    {/* --- NEW ATTRACTIVE HEADER --- */}
      {/* 1. Increased top padding to pt-24 (96px) to clear your 80px navbar */}
      <div className="bg-white border-b border-slate-200 pt-8 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
          {/* 2. Moved the button into the normal flow (no 'absolute') */}
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-500 hover:text-ink transition-colors mb-4" // 3. Added margin-bottom
          >
            <FaChevronLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
          {/* ------------------------------- */}
          <h1 className="text-3xl sm:text-5xl font-extrabold text-ink mb-4">
            Mutual Fund Dashboard
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            {/* Fixed the loading text */}
            Explore {allFunds.length > 0 ? `${allFunds.length.toLocaleString()}` : ''}+ schemes, analyze top performers, and search our entire database.
          </p>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} // <-- TYPO FIX
              placeholder="Search by fund name (e.g., 'Axis Bluechip')..."
              className="w-full p-4 pl-12 rounded-lg border border-slate-300 shadow-sm focus:ring-2 focus:ring-brand focus:outline-none text-lg"
            />
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <FaSearch className="text-gray-400" />
            </div>
          </div>
          {searchTerm.length > 0 && searchTerm.length < 3 && (
            <p className="text-sm text-indigo-200 mt-2">
              Type at least 3 characters to search...
            </p>
          )}
        </div>
      </div>

      {/* --- Main Content Area --- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* THIS IS THE KEY UI LOGIC: */}
        {searchTerm.length < 3 
          ? <DashboardContent />    // Show dashboard by default
          : <SearchResultsContent /> // Show results when user searches
        }
      </div>
    </div>
  );
}

export default MutualFundDashboard;